class ShopifyRecentlyViewed {
  constructor() {
    this.config = {
      howManyToStoreInMemory: 10,
      onComplete: null,
    };
    this.cookieName = 'shopify_recently_viewed';
    this.cookieConfiguration = {
      expires: 90,
      path: '/',
      domain: window.location.hostname,
    };

    this.#recordRecentlyViewed();
  }

  #cookieRead() {
    const cookieValue = getCookie(this.cookieName);
    let recentlyViewed = [];

    if (cookieValue) {
      recentlyViewed = cookieValue.split(' ');
    }
    return recentlyViewed;
  }

  #cookieWrite(recentlyViewed) {
    setCookie(this.cookieName, recentlyViewed.join(' '), this.cookieConfiguration);
  }

  #recordRecentlyViewed() {
    const recentlyViewed = this.#cookieRead();

    if (window.location.pathname.includes('/products/')) {
      const productHandle = window.location.pathname.match(/\/products\/([a-z0-9\-]+)/)[1];
      const position = recentlyViewed.findIndex(handle => handle === productHandle);

      if (position === -1) {
        recentlyViewed.unshift(productHandle);
        recentlyViewed.splice(this.config.howManyToStoreInMemory);
      } else {
        recentlyViewed.splice(position, 1);
        recentlyViewed.unshift(productHandle);
      }

      this.#cookieWrite(recentlyViewed);
    }
  }

  async getRecentlyViewed() {
    const recentlyViewed = this.#cookieRead();

    if (!recentlyViewed.length) return [];

    const responses = await Promise.all(
      recentlyViewed.map(handle =>
        fetch(`/products/${handle}?view=json`).then(response => (response.status === 404 ? null : response.json())),
      ),
    );

    return responses.filter(response => response);
  }
}

class EventEmitter {
  #events = new Map();

  subscribe(event, callback) {
    if (!this.#events.has(event)) {
      this.#events.set(event, []);
    }
    this.#events.get(event).push(callback);
  }

  emit(event, data) {
    const callbacks = this.#events.get(event);
    if (callbacks) {
      callbacks.forEach(callback => callback(data));
    }
  }

  listeners(event) {
    const callbacks = this.#events.get(event);
    if (!callbacks) return [];
    return callbacks;
  }

  unsubscribe(event, callback) {
    const callbacks = this.#events.get(event);
    if (!callbacks) return;
    this.#events.set(
      event,
      callbacks.filter(cb => cb !== callback),
    );
  }

  unsubscribeAll(event) {
    if (event) {
      this.#events.delete(event);
    } else {
      this.#events.clear();
    }
  }
}

class ErrorHandler {
  constructor(nextHandler = null) {
    this.nextHandler = nextHandler;
  }

  handleError(error) {
    if (this?.nextHandler) {
      return this.nextHandler.handleError(error);
    }
    return false;
  }
}

class AlertErrorHandler extends ErrorHandler {
  handleError(error) {
    alert(error.message);
    return super.handleError(error);
  }
}

class LogErrorHandler extends ErrorHandler {
  handleError(error) {
    console.error('Error:', error.message);
    console.trace();
    return super.handleError(error);
  }
}

class ShopifyService {
  #apiVersion = '2024-07';
  #apiToken = '';
  #graphqlUrl = '';

  constructor(apiToken) {
    this.#apiToken = apiToken;
    this.#graphqlUrl = `https://aldkitchen.myshopify.com/api/${this.#apiVersion}/graphql`;
  }

  fetchProduct(handle) {
    return fetch(`/products/${handle}?view=json`).then(response => (response.status === 404 ? null : response.json()));
  }

  async #queryGraphql(query) {
    const response = await fetch(this.#graphqlUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Shopify-Storefront-Access-Token': this.#apiToken,
      },
      body: JSON.stringify({query}),
    });

    if (response.status === 404) return null;
    return response.json();
  }

  fetchCollections(ids) {
    const query = `
      {
        nodes(ids: ${JSON.stringify(ids)}) {
          ...on Collection {
            id,
            handle,
            title,
            products (first: 250) {
              edges {
                node {
                  id,
                  handle,
                  title,
                  variants (first: 100) {
                    edges {
                      node {
                        id,
                        availableForSale,
                        title,
                        price {
                          amount,
                          currencyCode
                        },
                        sku,
                        image {
                          originalSrc
                        }
                      }
                    }
                  }
                }
              }
            }
          }
        }
      }
    `;
    return this.#queryGraphql(query);
  }

  fetchProducts(ids) {
    const query = `
      {
        nodes(ids: ${JSON.stringify(ids)}) {
          ...on Product {
            id,
            handle,
            title,
            free_gift_gid: metafield(namespace: "cart", key: "free_gift") {
              value
            }
            collections (first: 100) {
              edges {
                node {
                  id,
                  handle
                }
              }
            },
            variants (first: 100) {
              edges {
                node {
                  id,
                  availableForSale,
                  title,
                  price {
                    amount,
                    currencyCode
                  },
                  sku,
                  image {
                    originalSrc
                  },
                  arrives: metafield(namespace: "aldkitchen", key: "arrives") {
                    value
                  },
                  restock: metafield(namespace: "aldkitchen", key: "restocking_date") {
                    value
                  },
                  ships_from: metafield(namespace: "aldkitchen", key: "ships_from") {
                    value
                  }
                }
              }
            }
          }
        }
      }
    `;
    return this.#queryGraphql(query);
  }

  fetchProductVariants(ids) {
    const query = `{
      nodes(ids: ${JSON.stringify(ids)}) {
        ...on ProductVariant {
          id,
          title,
          availableForSale,
          price {
            amount,
            currencyCode
          },
          sku,
          image {
            originalSrc
          },
          product {
            id,
            handle,
            title,
            free_gift_gid: metafield(namespace: "cart", key: "free_gift") {
              value
            },
            featuredImage {
              originalSrc
            },
            collections (first: 100) {
              edges {
                node {
                  id,
                  handle
                }
              }
            },
          },
          arrives: metafield(namespace: "aldkitchen", key: "arrives") {
            value
          },
          restock: metafield(namespace: "aldkitchen", key: "restocking_date") {
            value
          },
          ships_from: metafield(namespace: "aldkitchen", key: "ships_from") {
            value
          }
        }
      }
    }`;
    return this.#queryGraphql(query);
  }
}

class CartApiService {
  #apiRoutes = {
    get: Shopify.routes.cart_url,
    clear: Shopify.routes.cart_clear_url,
    add: Shopify.routes.cart_add_url,
    change: Shopify.routes.cart_change_url,
    update: Shopify.routes.cart_update_url,
  };

  #fetchConfig(method = 'POST', type = 'json') {
    return {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: `application/${type}`,
      },
    };
  }

  #sendRequest(url, body = {}) {
    const options = {
      ...this.#fetchConfig(url === this.#apiRoutes.get ? 'GET' : 'POST'),
      body: JSON.stringify(body),
    };
    return fetch(url, options).then(response => response.json());
  }

  get(body) {
    return this.#sendRequest(this.#apiRoutes.get, body);
  }

  clear(body) {
    return this.#sendRequest(this.#apiRoutes.clear, body);
  }

  add(body) {
    return this.#sendRequest(this.#apiRoutes.add, body);
  }

  change(body) {
    return this.#sendRequest(this.#apiRoutes.change, body);
  }

  update(body) {
    return this.#sendRequest(this.#apiRoutes.update, body);
  }
}

class DiscountApiService {
  #apiRoutes = {
    apply: '/discount/',
    clear: '/discount/clear',
  };

  #sendRequest(url) {
    return fetch(url);
  }

  apply(code) {
    const url = `${this.#apiRoutes.apply}${encodeURIComponent(code)}`;
    return this.#sendRequest(url);
  }

  clear() {
    return this.#sendRequest(this.#apiRoutes.clear);
  }
}

class CartManager {
  #events = {
    updated: 'ald:cart:updated',
  };

  constructor(apiService, errorHandler, eventEmitter, freeGiftManager) {
    this.apiService = apiService;
    this.errorHandler = errorHandler;
    this.eventEmitter = eventEmitter;
    this.freeGiftManager = freeGiftManager;
  }

  get() {
    return this.#performAction('get');
  }

  clear(body) {
    return this.#performAction('clear', body);
  }

  add(body) {
    return this.#performAction('add', body);
  }

  update(body) {
    return this.#performAction('update', body);
  }

  setAttributes(attributes) {
    return this.update({attributes});
  }

  setNote(note) {
    return this.update({note});
  }

  onUpdated(callback) {
    this.eventEmitter.subscribe(this.#events.updated, callback);
  }

  async #performAction(action, body) {
    const mutatedBody = clone(body);

    if (['add', 'update'].includes(action)) {
      await this.freeGiftManager.addFreeGiftsToCart(mutatedBody, this.get.bind(this));
    }

    return this.apiService[action](mutatedBody)
      .then(data => {
        action !== 'get' && this.#triggerUpdated(data);
        return data;
      })
      .catch(this.errorHandler.handleError);
  }

  #triggerUpdated(cart) {
    this.eventEmitter.emit(this.#events.updated, cart);
  }
}

class DiscountManager {
  #discountCodes = {
    '5% coupon': {
      type: 'order',
      value: {
        type: 'percentage',
        value: 5,
      },
    },
    MIXES_: {
      type: 'product',
      value: {
        type: 'percentage',
        value: 5,
      },
      collections: [228735189147],
    },
  };
  #events = {
    updated: 'ald:discount:updated',
  };

  constructor(apiService, errorHandler, eventEmitter) {
    this.apiService = apiService;
    this.eventEmitter = eventEmitter;
    this.errorHandler = errorHandler;
  }

  getPriceWithDiscount(price, discountCode) {
    const discount = this.#getDiscountDetails(discountCode);
    if (!discount) return price;

    const {type, value} = discount.value;
    if (type === 'percentage') {
      return Math.round(price * (1 - value / 100));
    } else if (type === 'amount') {
      return price - value;
    }
    return price;
  }

  getCurrentDiscountCode() {
    return Shopify.cartAttributes?._discount_code;
  }

  apply(code) {
    return this.#performAction('apply', this.#events.updated, code);
  }

  clear() {
    return this.#performAction('clear', this.#events.updated);
  }

  onUpdated(callback) {
    this.eventEmitter.subscribe(this.#events.updated, callback);
  }

  isProductEligibleForDiscount(product, discountCode) {
    const discount = this.#getDiscountDetails(discountCode);
    if (!discount || !product) return false;

    return this.#isOrderDiscount(discount) || this.#isProductDiscountApplicable(product, discount);
  }

  #isOrderDiscount(discount) {
    return discount.type === 'order';
  }

  #isProductDiscountApplicable(product, discount) {
    const productCollectionIds = product.collections?.map(collection => collection.id) || [];
    const isInDiscountedCollection = discount.collections?.some(id => productCollectionIds.includes(id) || false);
    const isDirectProductMatch = discount.products?.includes(product.id) || false;

    return isInDiscountedCollection || isDirectProductMatch;
  }

  #getDiscountDetails(discountCode) {
    if (!discountCode) return null;
    const key = Object.keys(this.#discountCodes).find(key => discountCode.includes(key));
    return this.#discountCodes[key];
  }

  #performAction(action, eventName, discountCode) {
    return this.apiService[action](discountCode)
      .then(() => this.eventEmitter.emit(eventName, discountCode))
      .catch(this.errorHandler.handleError);
  }
}

class FreeGiftManager {
  constructor(shopifyService, errorHandler) {
    this.errorHandler = errorHandler;
    this.shopifyService = shopifyService;
    this.productCache = new Map();
  }

  async addFreeGiftsToCart(body, fetchCart) {
    const field = body.items ? 'items' : 'updates';
    const variantIds = this.extractVariantIds(body, field);

    const variants = await this.fetchProductVariantsWithCache(variantIds);
    const freeGiftMap = await this.buildFreeGiftMapForVariants(variants);

    await this.updateCartWithFreeGifts(body, field, variantIds, freeGiftMap, fetchCart);
  }

  extractVariantIds(body, field) {
    const extractProductId = id => this.idToProductVariantGid(id.toString().split(':').shift());

    if (field === 'items') {
      return Array.from(new Set(body.items.map(item => extractProductId(item.id))));
    } else if (field === 'updates') {
      return Object.keys(body.updates).map(extractProductId);
    }
    return [];
  }

  async buildFreeGiftMapForVariants(variants) {
    const freeGiftProductIds = variants.map(({product}) => product.free_gift_gid?.value).filter(Boolean);

    const freeGiftProducts = await this.fetchProductsWithCache(freeGiftProductIds);
    const freeGiftVariantsMap = this.buildFreeGiftVariantsMap(freeGiftProducts);

    return this.buildFreeGiftMap(variants, freeGiftVariantsMap);
  }

  async updateCartWithFreeGifts(body, field, variantIds, freeGiftMap, fetchCart) {
    for (const variantId of variantIds) {
      const freeGiftId = freeGiftMap.get(variantId);

      if (!freeGiftId) continue;

      if (field === 'items') {
        const relatedItem = body.items.find(item => item.id.includes(this.gidToId(variantId)));

        body.items.push({
          id: this.gidToId(freeGiftId),
          quantity: relatedItem.quantity,
          properties: {
            _free_gift_for: relatedItem.id,
            'Free gift for': relatedItem.id,
          },
        });
      } else if (field === 'updates') {
        const cart = await fetchCart();
        const relatedItemId = this.gidToId(variantId);
        const relatedItemKey = Object.keys(body.updates).find(key => key.includes(relatedItemId));
        const freeGiftItem = cart.items.find(item => item.properties._free_gift_for?.includes(relatedItemId));

        if (freeGiftItem) {
          body.updates[freeGiftItem.key] = body.updates[relatedItemKey];
        }
      }
    }
  }

  async fetchWithCache(ids, fetchFunction) {
    const cachedItems = ids.map(id => this.productCache.get(id)).filter(Boolean);
    const missingIds = ids.filter(id => !this.productCache.has(id));

    if (missingIds.length > 0) {
      try {
        const response = await fetchFunction(missingIds);
        const fetchedItems = response.data?.nodes?.filter(Boolean) || [];
        fetchedItems.forEach(item => this.productCache.set(item.id, item));
        return [...cachedItems, ...fetchedItems];
      } catch (error) {
        this.errorHandler.handleError(error);
        return cachedItems;
      }
    }
    return cachedItems;
  }

  async fetchProductsWithCache(productIds) {
    return this.fetchWithCache(productIds, this.shopifyService.fetchProducts.bind(this.shopifyService));
  }

  async fetchProductVariantsWithCache(variantIds) {
    return this.fetchWithCache(variantIds, this.shopifyService.fetchProductVariants.bind(this.shopifyService));
  }

  buildFreeGiftMap(variants, freeGiftVariantsMap) {
    const freeGiftMap = new Map();

    for (const variant of variants) {
      if (variant.product.free_gift_gid?.value) {
        const freeGiftVariantId = freeGiftVariantsMap.get(variant.product.free_gift_gid.value);
        if (freeGiftVariantId) {
          freeGiftMap.set(variant.id, freeGiftVariantId);
        }
      }
    }

    return freeGiftMap;
  }

  buildFreeGiftVariantsMap(freeGiftProducts) {
    return new Map(
      freeGiftProducts
        .map(freeGiftProduct => {
          const freeGiftVariant = freeGiftProduct.variants.edges.find(({node}) => node.availableForSale);
          return freeGiftVariant ? [freeGiftProduct.id, freeGiftVariant.node.id] : [freeGiftProduct.id, null];
        })
        .filter(([, value]) => value !== null),
    );
  }

  gidToId(gid) {
    if (!gid) return null;

    const idString = gid.replace(/gid:\/\/shopify\/(ProductVariant|Product)\//, '');
    const idNumber = Number(idString);

    return isNaN(idNumber) ? null : idNumber;
  }

  idToProductVariantGid(id) {
    return id
      ? id.toString().startsWith('gid://shopify/ProductVariant/')
        ? id
        : `gid://shopify/ProductVariant/${id}`
      : null;
  }
}

(() => {
  const cartApiService = new CartApiService();
  const discountApiService = new DiscountApiService();
  // const errorHandler = new LogErrorHandler(new AlertErrorHandler());
  const errorHandler = new LogErrorHandler();
  const eventEmitter = new EventEmitter();
  const shopifyService = new ShopifyService('601838204b33f602c72b88e52f8652bc');
  const discountManager = new DiscountManager(discountApiService, errorHandler, eventEmitter);
  const freeGiftManager = new FreeGiftManager(shopifyService, errorHandler);
  const cartManager = new CartManager(cartApiService, errorHandler, eventEmitter, freeGiftManager);
  const recentlyViewed = new ShopifyRecentlyViewed();

  setupGlobalVariables();
  setupEventListeners();

  if (Shopify.cartAttributes?._discount_code) {
    discountManager.apply(Shopify.cartAttributes?._discount_code);
  }

  if (location.pathname === '/policies/terms-of-service') {
    createNavOnTermPage();
  }

  function setupEventListeners() {
    discountManager.onUpdated(event => cartManager.setAttributes({_discount_code: event || null}));
  }

  function setupGlobalVariables() {
    window.Shopify = window.Shopify || {};
    window.monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sept', 'Oct', 'Nov', 'Dec'];
    window.isIOS = (() =>
      ['iPad Simulator', 'iPhone Simulator', 'iPod Simulator', 'iPad', 'iPhone', 'iPod'].includes(navigator.platform) ||
      (navigator.userAgent.includes('Mac') && 'ontouchend' in document))();
    window.showProductsByVoltage = showProductsByVoltage;

    Date.prototype.mmdd = function () {
      const month = window.monthNames[this.getMonth()];
      const day = String(this.getDate());
      return `${month} ${day}`;
    };

    Shopify.discount = discountManager;
    Shopify.cart = cartManager;
    Shopify.shopifyService = shopifyService;
    Shopify.recentlyViewed = recentlyViewed;
    Shopify.eventEmitter = eventEmitter;

    // TODO: переделать на класс
    Shopify.bind = function (fn, scope) {
      return function () {
        return fn.apply(scope, arguments);
      };
    };

    Shopify.setSelectorByValue = function (selector, value) {
      for (var i = 0, count = selector.options.length; i < count; i++) {
        var option = selector.options[i];

        if (value == option.value || value == option.innerHTML) {
          selector.selectedIndex = i;
          return i;
        }
      }
    };

    Shopify.addListener = function (target, eventName, callback) {
      target.addEventListener
        ? target.addEventListener(eventName, callback, false)
        : target.attachEvent('on' + eventName, callback);
    };

    Shopify.postLink = function (path, options) {
      options = options || {};
      var method = options['method'] || 'post';
      var params = options['parameters'] || {};
      var form = document.createElement('form');
      form.setAttribute('method', method);
      form.setAttribute('action', path);

      for (var key in params) {
        var hiddenField = document.createElement('input');
        hiddenField.setAttribute('type', 'hidden');
        hiddenField.setAttribute('name', key);
        hiddenField.setAttribute('value', params[key]);
        form.appendChild(hiddenField);
      }

      document.body.appendChild(form);
      form.submit();
      document.body.removeChild(form);
    };

    Shopify.CountryProvinceSelector = function (country_domid, province_domid, options) {
      this.countryEl = document.getElementById(country_domid);
      this.provinceEl = document.getElementById(province_domid);
      this.provinceContainer = document.getElementById(options['hideElement'] || province_domid);

      Shopify.addListener(this.countryEl, 'change', Shopify.bind(this.countryHandler, this));
      this.initCountry();
      this.initProvince();
    };

    Shopify.CountryProvinceSelector.prototype = {
      initCountry: function () {
        var value = this.countryEl.getAttribute('data-default');
        Shopify.setSelectorByValue(this.countryEl, value);
        this.countryHandler();
      },
      initProvince: function () {
        var value = this.provinceEl.getAttribute('data-default');

        if (value && this.provinceEl.options.length > 0) {
          Shopify.setSelectorByValue(this.provinceEl, value);
        }
      },
      countryHandler: function (e) {
        var opt = this.countryEl.options[this.countryEl.selectedIndex];
        var raw = opt?.getAttribute('data-provinces') || '[]';
        var provinces = JSON.parse(raw);
        this.clearOptions(this.provinceEl);

        if (provinces && provinces.length == 0) {
          this.provinceContainer.style.display = 'none';
        } else {
          for (var i = 0; i < provinces.length; i++) {
            var opt = document.createElement('option');
            opt.value = provinces[i][0];
            opt.innerHTML = provinces[i][1];
            this.provinceEl.appendChild(opt);
          }

          this.provinceContainer.style.display = '';
        }
      },
      clearOptions: function (selector) {
        while (selector.firstChild) {
          selector.removeChild(selector.firstChild);
        }
      },
      setOptions: function (selector, values) {
        for (var i = 0, count = values.length; i < values.length; i++) {
          var opt = document.createElement('option');
          opt.value = values[i];
          opt.innerHTML = values[i];
          selector.appendChild(opt);
        }
      },
    };
  }

  function createNavOnTermPage() {
    const container = document.querySelector('.shopify-policy__body');
    const content = document.querySelector('.shopify-policy__body .rte');

    if (!container || !content) return;

    const allH2 = content.querySelectorAll('h2');

    if (!allH2.length) return;

    const nav = document.createElement('div');
    nav.classList.add('nav');

    allH2.forEach((item, index) => {
      item.id = `anchor_${index}`;
      item.setAttribute('style', 'scroll-margin-top: 17rem;');

      const link = document.createElement('a');
      link.href = `#${item.id}`;
      link.innerText = item.innerText;
      nav.appendChild(link);
    });

    container.setAttribute('style', 'display: flex; gap: 10rem;');
    container.prepend(nav);
  }

  function showProductsByVoltage() {
    const productItems = document.querySelectorAll('.collection-page__grid .grid__item');

    productItems.forEach(el => {
      const voltage = Shopify.currentCountry?.voltage ?? '110V';
      const variantTitle = el.getAttribute('data-variant-title');

      if (!variantTitle?.includes(voltage === '110V' ? '220V' : '110V')) {
        el.setAttribute('style', 'display: flex');
      }
    });
  }
})();
